/**
 * ADA Fix: Proper ARIA Roles for Carousels
 * 
 * Issue: Carousels were using inappropriate ARIA tab roles (tabpanel) when they
 * should use proper carousel roles.
 * 
 * Recommendation: Use W3C carousel pattern:
 * - Container: role="region" aria-roledescription="carousel"
 * - Items: role="group" aria-roledescription="slide" aria-label="X of Y"
 * 
 * Reference:
 * - https://www.w3.org/WAI/tutorials/carousels/working-example/
 * - https://codepen.io/A360/pen/KKaOYwY
 * 
 * This script ensures carousel items have proper slide roles.
 */

(function() {
  'use strict';

  console.log('🔧 ADA Fix: Adding proper ARIA carousel roles...');

  /**
   * Carousel selectors with role="region" aria-roledescription="carousel"
   */
  const CAROUSEL_SELECTORS = [
    '[role="region"][aria-roledescription="carousel"]',
    'loess-scroller[role="region"]',
    '.splide[role="region"]'
  ];

  /**
   * Process carousel items to add slide roles
   */
  function processCarouselItems(carousel) {
    // Find the list of items
    const itemsContainer = carousel.querySelector('[role="list"], ul.scroller, ul');
    
    if (!itemsContainer) {
      console.warn('⚠️  Carousel found but no items container:', carousel);
      return;
    }

    // Get all list items
    const items = Array.from(itemsContainer.querySelectorAll('li'));
    
    if (items.length === 0) {
      console.warn('⚠️  Carousel found but no items:', carousel);
      return;
    }

    const totalSlides = items.length;
    let processedCount = 0;

    items.forEach((item, index) => {
      // Skip if already processed
      if (item.hasAttribute('data-carousel-slide')) {
        return;
      }

      // Add slide role and attributes
      item.setAttribute('role', 'group');
      item.setAttribute('aria-roledescription', 'slide');
      item.setAttribute('aria-label', `${index + 1} of ${totalSlides}`);
      item.setAttribute('data-carousel-slide', 'true');

      processedCount++;
    });

    console.log(`✅ Processed carousel: ${processedCount}/${totalSlides} slides`);

    return {
      total: totalSlides,
      processed: processedCount
    };
  }

  /**
   * Process all carousels
   */
  function processAllCarousels() {
    let carouselCount = 0;
    let totalSlides = 0;

    CAROUSEL_SELECTORS.forEach(selector => {
      const carousels = document.querySelectorAll(selector);
      
      carousels.forEach(carousel => {
        if (!carousel.hasAttribute('data-carousel-aria-processed')) {
          const stats = processCarouselItems(carousel);
          
          if (stats) {
            carousel.setAttribute('data-carousel-aria-processed', 'true');
            carouselCount++;
            totalSlides += stats.total;

            // Log carousel info
            console.log('🎠 Processed carousel:', {
              label: carousel.getAttribute('aria-label'),
              slides: stats.total,
              tag: carousel.tagName
            });
          }
        }
      });
    });

    if (carouselCount > 0) {
      console.log(`✅ Processed ${carouselCount} carousel(s) with ${totalSlides} total slides`);
    }

    return {
      carousels: carouselCount,
      slides: totalSlides
    };
  }

  /**
   * Validate carousel ARIA
   */
  function validateCarousels() {
    const issues = [];

    // Find all carousels
    CAROUSEL_SELECTORS.forEach(selector => {
      const carousels = document.querySelectorAll(selector);
      
      carousels.forEach(carousel => {
        const carouselLabel = carousel.getAttribute('aria-label') || 'Unknown';
        
        // Check if carousel has proper role
        if (carousel.getAttribute('role') !== 'region') {
          issues.push({
            carousel: carouselLabel,
            issue: 'Missing role="region"'
          });
        }

        // Check if carousel has aria-roledescription
        if (carousel.getAttribute('aria-roledescription') !== 'carousel') {
          issues.push({
            carousel: carouselLabel,
            issue: 'Missing aria-roledescription="carousel"'
          });
        }

        // Check if items have slide roles
        const items = carousel.querySelectorAll('[role="list"] > li, ul.scroller > li');
        let itemsWithoutSlideRole = 0;

        items.forEach(item => {
          if (item.getAttribute('role') !== 'group' ||
              item.getAttribute('aria-roledescription') !== 'slide') {
            itemsWithoutSlideRole++;
          }
        });

        if (itemsWithoutSlideRole > 0) {
          issues.push({
            carousel: carouselLabel,
            issue: `${itemsWithoutSlideRole} items missing slide roles`
          });
        }
      });
    });

    if (issues.length === 0) {
      console.log('✅ Validation passed: All carousels have proper ARIA roles');
      return true;
    } else {
      console.warn('⚠️  Validation: Some carousels have ARIA issues:');
      console.table(issues);
      return false;
    }
  }

  /**
   * Setup mutation observer for dynamic carousels
   */
  function setupMutationObserver() {
    const observer = new MutationObserver(function(mutations) {
      let shouldProcess = false;

      mutations.forEach(function(mutation) {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(function(node) {
            if (node.nodeType === 1) {
              // Check if it's a carousel or contains carousels
              CAROUSEL_SELECTORS.forEach(selector => {
                try {
                  if (node.matches && node.matches(selector)) {
                    shouldProcess = true;
                  } else if (node.querySelector && node.querySelector(selector)) {
                    shouldProcess = true;
                  }
                } catch (e) {
                  // Invalid selector
                }
              });
            }
          });
        }
      });

      if (shouldProcess) {
        setTimeout(function() {
          console.log('🔄 Processing carousels due to DOM changes...');
          processAllCarousels();
        }, 300);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    return observer;
  }

  /**
   * Main initialization function
   */
  function initialize() {
    console.log('🚀 Starting carousel ARIA roles fix...');

    // Process existing carousels
    const stats = processAllCarousels();

    // Setup mutation observer
    setupMutationObserver();

    // Validate after delay
    setTimeout(function() {
      validateCarousels();
    }, 2000);

    console.log('✅ Carousel ARIA roles initialized');
    console.log(`📊 Carousels: ${stats.carousels}, Slides: ${stats.slides}`);

    return stats;
  }

  // Run on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }

  // Also run after delays for dynamic content
  setTimeout(initialize, 1000);
  setTimeout(initialize, 2000);

  // Expose for testing
  window.adaCarouselAriaRoles = {
    processCarouselItems,
    processAllCarousels,
    validateCarousels,
    initialize
  };

  console.log('🔧 ADA Carousel ARIA Roles loaded. Functions available in window.adaCarouselAriaRoles');

})();
